import {useParams} from "react-router-dom";
import SettingsHeader from "../SettingsHeader";
import {Alert, Button, Checkbox, FormControlLabel, FormGroup, LinearProgress} from "@mui/material";
import {ShopTabs} from "../ShopTabs";
import * as React from "react";
import {useNavigate} from "react-router";
import {
   BrandSettingConsumerInput,
   ConsumerAuthMethodEnum,
   useBrandSearchIndexesQuery,
   useConsumerSettingsQuery,
   useConsumerSettingsSaveMutation
} from "../../../Queries";
import {SavingUI} from "../../../SavingProvider";
import {Controller, FormProvider, useForm} from "react-hook-form";
import PageContent from "../../../layout/PageContent";
import {BodyText, TitleText} from "../../../layout/Typography";
import {FormInputCheckbox} from "../../shop/wholesale/form/FormInputCheckbox";
import {FormInputImage} from "../../shop/wholesale/form/FormInputImage";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import Grid from "@mui/material/Grid";
import {FormInputSelect, SelectOption} from "../../shop/wholesale/form/FormInputSelect";
import {FormInputPrice} from "../../shop/wholesale/form/FormInputPrice";
import {FormInputNumber} from "../../shop/wholesale/form/FormInputNumber";

const ID_CONSUMERFORM = "SettingsConsumers";

const AUTH_METHOD_OPTIONS: { method: ConsumerAuthMethodEnum; label: string; disabled?: boolean }[] = [
   {method: ConsumerAuthMethodEnum.MAIL_LINK, label: "Email link"},
   {method: ConsumerAuthMethodEnum.SMS_CODE, label: "SMS code"},
   {method: ConsumerAuthMethodEnum.MAIL_CODE, label: "Email code", disabled: true},
];

const ConsumerSettingsPagePath = (brandId: string, targetId: string) => {
   return "/brands/" + brandId + "/settings/appsettings/" + targetId
}

export const TradeInSettingsPage = () => {
   const {brandHouseId, edit} = useParams<{ brandHouseId: string, edit: string }>();
   const navigate = useNavigate();

   const handleChange = (value: string) => {
      navigate(ConsumerSettingsPagePath(brandHouseId, value));
   };

   return <div>
      <SettingsHeader title={"General settings"} subtitle={""}>
         <Button type="submit" form={ID_CONSUMERFORM} variant={"contained"}>Save</Button>
      </SettingsHeader>

      <ShopTabs
         activeId={edit}
         brandHouseId={brandHouseId}
         onChange={(value) => handleChange(value)}
         renderDefault={() => <ConsumerSettingsInner brandId={brandHouseId} brandHouseId={brandHouseId}/>}
         renderShop={(shop) => <ConsumerSettingsInner brandId={brandHouseId} shopId={shop}/>}
      />
   </div>
}

type ConsumerSettingsInnerProps = {
   brandId: string
   brandHouseId?: string
   shopId?: string
}

const ConsumerSettingsInner = ({brandId, brandHouseId, shopId}: ConsumerSettingsInnerProps) => {
   const {data} = useConsumerSettingsQuery({
      variables: {
         shopId: shopId,
         brandHouseId: brandHouseId
      },
      fetchPolicy: "no-cache"
   })
   const {data: searchIndexes} = useBrandSearchIndexesQuery({
      variables: {
         brandHouseId: brandId
      }
   });

   const [saveSettings] = useConsumerSettingsSaveMutation()

   if (!data || !searchIndexes) {
      return <LinearProgress/>
   }

   const onSave = async (data: ConsumerFormData) => {
      await SavingUI.process(async () => {
         const {
            defaultProductImage,
            tradeIn2InvalidEmailExp,
            tradeInProductSearch,
            tradeInProductSearchMask,
            tradeInProductSearchGroupId,
            tradeInProductSearchImagesOnly,
            lockEmail,
            authMethods,
            tradeInHideLanguageSelector,
            tradeInComments,
            tradeInPayoutEnabled,
            tradeInPayoutPct,
            digitalPassportActive,
            tradeInPointsRate,
            tradeInPointsEnabled,
            tradeInVoucherDeduction,
            tradeInLoginWithoutData
         } = data;
         await saveSettings({
            variables: {
               shopId: shopId,
               brandHouseId: brandHouseId,
               input: {
                  consumer: {
                     lockEmail,
                     authMethods
                  },
                  defaultProductImage,
                  tradeIn2InvalidEmailExp,
                  tradeInProductSearch,
                  tradeInProductSearchMask,
                  tradeInProductSearchGroupId: tradeInProductSearchGroupId || null,
                  tradeInProductSearchImagesOnly,
                  tradeInHideLanguageSelector,
                  tradeInComments,
                  tradeInPayoutEnabled,
                  tradeInPayoutPct,
                  digitalPassportActive,
                  tradeInPointsRate,
                  tradeInPointsEnabled,
                  tradeInVoucherDeduction,
                  tradeInLoginWithoutData
               }
            }
         });
      }, 'Consumer settings updated')
   }
   return <ConsumerInputEditor
      searchIndexes={searchIndexes!.BrandProductGroups!.map(g => ({id: g._id, name: g.name})) || []}
      container={(shopId || brandHouseId)!}
      data={{
         ...data!.BrandSettings!.consumer,
         authMethods: (data!.BrandSettings!.consumer?.authMethods?.filter(Boolean) || []) as ConsumerAuthMethodEnum[],
         defaultProductImage: data!.BrandSettings!.defaultProductImage,
         tradeIn2InvalidEmailExp: data!.BrandSettings!.tradeIn2InvalidEmailExp,
         tradeInProductSearch: data!.BrandSettings!.tradeInProductSearch,
         tradeInProductSearchMask: data!.BrandSettings!.tradeInProductSearchMask,
         tradeInProductSearchGroupId: data!.BrandSettings!.tradeInProductSearchGroupId,
         tradeInProductSearchImagesOnly: data!.BrandSettings!.tradeInProductSearchImagesOnly,
         tradeInHideLanguageSelector: data!.BrandSettings!.tradeInHideLanguageSelector,
         tradeInComments: data!.BrandSettings!.tradeInComments,
         tradeInPayoutEnabled: data!.BrandSettings!.tradeInPayoutEnabled,
         tradeInPayoutPct: data!.BrandSettings!.tradeInPayoutPct,
         digitalPassportActive: data!.BrandSettings!.digitalPassportActive,
         tradeInPointsRate: data!.BrandSettings!.tradeInPointsRate,
         tradeInPointsEnabled: data!.BrandSettings!.tradeInPointsEnabled,
         tradeInVoucherDeduction: data!.BrandSettings!.tradeInVoucherDeduction,
         tradeInLoginWithoutData: data!.BrandSettings!.tradeInLoginWithoutData
      } as ConsumerFormData}
      currency={data!.BrandSettings!.shopCurrency || undefined}
      onSave={onSave}
   />
}


type ConsumerFormData = Omit<BrandSettingConsumerInput, 'authMethods'> & {
   authMethods: ConsumerAuthMethodEnum[]
   defaultProductImage: string
   tradeIn2InvalidEmailExp: string
   tradeInProductSearch: boolean
   tradeInProductSearchMask?: string
   tradeInProductSearchGroupId: string
   tradeInProductSearchImagesOnly: boolean
   tradeInHideLanguageSelector: boolean
   tradeInComments: boolean
   tradeInPayoutEnabled: boolean
   tradeInPayoutPct: number
   digitalPassportActive: boolean
   tradeInPointsRate: number
   tradeInPointsEnabled: boolean
   tradeInVoucherDeduction: boolean
   tradeInLoginWithoutData: boolean
}

type ConsumerInputEditorProps = {
   searchIndexes: SelectOption[]
   container: string
   currency?: string
   data?: ConsumerFormData
   onSave: (data: ConsumerFormData) => Promise<void>
}

const ConsumerInputEditor = ({container, data, searchIndexes, currency, onSave}: ConsumerInputEditorProps) => {
   const methods = useForm<ConsumerFormData>({
      defaultValues: data
   });
   const {control, handleSubmit} = methods;

   // @ts-ignore
   return <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSave)} id={ID_CONSUMERFORM}/>
      <PageContent>
         <TitleText type={"h2"}>Product search</TitleText>
         <BodyText type={"subtitle1"}>
            Allow consumers to add items to their tradein directly from brand
            masterdata.
         </BodyText>
         <FormInputCheckbox name={"tradeInProductSearch"} label={"Enable search based trade-ins"}/>
         <Grid item container spacing={1} sx={{paddingLeft: '30px'}}>
            <Grid item xs={12}>
               <FormInputText name={"tradeInProductSearchMask"} label={"Search mask (Example: ####-####)"}/>
            </Grid>
            <Grid item xs={12}>
               <FormInputSelect name={"tradeInProductSearchGroupId"} label={"Search index to use"}
                                options={searchIndexes}/>
            </Grid>
         </Grid>
         <FormInputCheckbox name={"tradeInProductSearchImagesOnly"} label={"Only show products that has valid images"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Digital passport tradeins</TitleText>
         <FormInputCheckbox name={"digitalPassportActive"} label={"Enable the use of digital passport tradeins"}/>
      </PageContent>
      {currency && <PageContent>
         <TitleText type={"h2"}>Points in TradeIn</TitleText>
         <FormInputCheckbox name={`tradeInPointsEnabled`}
                            label={"Use POINT as tradein currency (requires POINT integration)"}/>
         <Grid item container spacing={1} sx={{paddingLeft: '30px'}}>
            <Grid item xs={12}>
               <FormInputPrice name={`tradeInPointsRate`} label={"Enter what 1 " + currency + ' is in POINT"'}/>
            </Grid>
         </Grid>
      </PageContent>}
      <PageContent>
         <TitleText type={"h2"}>Deduct Reward on Rejection</TitleText>
         <BodyText type={"subtitle1"}>
            If a trade-in has an item that is rejected, then the reward will be reduced to match the value of the accepted items
         </BodyText>
         <FormInputCheckbox name={`tradeInVoucherDeduction`}
                            label={"Deduct from reward"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Comments</TitleText>
         <FormInputCheckbox name={"tradeInComments"} label={"Enable trade-in comments"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Payout</TitleText>
         <FormInputCheckbox name={"tradeInPayoutEnabled"} label={"Enable payout selection by customer"}/>
         <Grid item container spacing={1} sx={{paddingLeft: '30px'}}>
            <Grid item xs={12}>
               <FormInputNumber name={"tradeInPayoutPct"} label={"Payout in % of reward value"}/>
            </Grid>
         </Grid>
      </PageContent>
      <PageContent>

         <TitleText type={"h2"}>Consumer profile settings</TitleText>
         <FormInputCheckbox name={"lockEmail"} label={"Disallow editing of email"}/>
         <Grid item container spacing={1} sx={{paddingLeft: '30px'}}>
            <Grid item xs={12}>
               <FormInputText name={"tradeIn2InvalidEmailExp"} label={"Regular expression (for disallowing e-mails)"}/>
            </Grid>
         </Grid>
         <FormInputCheckbox name={"tradeInHideLanguageSelector"} label={"Hide language selector in trade-in app"}/>
         <FormInputCheckbox name={"tradeInLoginWithoutData"} label={"Allow logins when consumer has no potentials"}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Authentication methods</TitleText>
         <BodyText type={"subtitle1"}>
            Select which authentication methods consumers can use to log in.
         </BodyText>
         <Controller
            name={"authMethods"}
            control={control}
            render={({field: {value, onChange}}) => {
               const selected: ConsumerAuthMethodEnum[] = value || [];
               const handleToggle = (method: ConsumerAuthMethodEnum) => {
                  if (selected.includes(method)) {
                     onChange(selected.filter(m => m !== method));
                  } else {
                     onChange([...selected, method]);
                  }
               };
               return <FormGroup>
                  {AUTH_METHOD_OPTIONS.map(({method, label, disabled}) => (
                     <FormControlLabel
                        key={method}
                        control={
                           <Checkbox
                              size={"small"}
                              checked={selected.includes(method)}
                              onChange={() => handleToggle(method)}
                              disabled={disabled}
                           />
                        }
                        label={label}
                     />
                  ))}
               </FormGroup>
            }}
         />
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Default product image</TitleText>
         <BodyText type={"subtitle1"}>If the masterdata does not have an original packshot image, this image can be used
            as a default</BodyText>
         {data?.defaultProductImage?.startsWith('http') && <>
            <Alert variant={"outlined"} color={"warning"} severity={"error"}>Default image is using old format</Alert>
         </>}
         <FormInputImage name={"defaultProductImage"} control={control} label={'Default image for consumers'}
                         container={container}/>
      </PageContent>
   </FormProvider>
}
